import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Day, LegMode, Stop, TravelMode, Trip } from '../../types';
import { serifVoice } from '../../theme';
import { MODE_LABELS, formatDay } from '../../lib/plan';

const LEG_MODES: LegMode[] = ['train', 'bus', 'flight', 'car', 'ferry', 'walk'];
const END_MODES: TravelMode[] = ['train', 'flight', 'bus', 'car', 'ferry'];

function ModeChips<M extends string>({ modes, value, onChange }: { modes: M[]; value: M | null; onChange: (m: M | null) => void }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      <Chip label="Not decided" onClick={() => onChange(null)} color={value === null ? 'primary' : 'default'} variant={value === null ? 'filled' : 'outlined'} />
      {modes.map((m) => (
        <Chip
          key={m}
          label={MODE_LABELS[m as unknown as LegMode]}
          onClick={() => onChange(m)}
          color={value === m ? 'primary' : 'default'}
          variant={value === m ? 'filled' : 'outlined'}
        />
      ))}
    </Box>
  );
}

/** "Set it": how to make a move between towns, and when to decide by. */
export function LegDialog({
  stop,
  onClose,
  onSave,
  onRemove,
}: {
  stop: Stop | null;
  onClose: () => void;
  onSave: (stop: Stop, patch: { leg_mode: LegMode | null; decide_by: string | null; title: string }) => void;
  onRemove: (stop: Stop) => void;
}) {
  const [mode, setMode] = useState<LegMode | null>(null);
  const [decideBy, setDecideBy] = useState('');
  const [title, setTitle] = useState('');

  useEffect(() => {
    if (stop) {
      setMode(stop.leg_mode);
      setDecideBy(stop.decide_by ?? '');
      setTitle(stop.title);
    }
  }, [stop]);

  return (
    <Dialog open={!!stop} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{stop?.title}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {stop?.context ? (
          <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'background.default' }}>
            <Typography variant="body2" sx={{ ...serifVoice, whiteSpace: 'pre-line' }}>
              {stop.context}
            </Typography>
          </Box>
        ) : null}
        <TextField label="From → to" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            How
          </Typography>
          <ModeChips modes={LEG_MODES} value={mode} onChange={setMode} />
        </Box>
        {!mode ? (
          <TextField
            label="Decide by"
            type="date"
            value={decideBy}
            onChange={(e) => setDecideBy(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="It stays on your Decide later list until then."
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={() => stop && onRemove(stop)} sx={{ mr: 'auto' }}>
          Remove this move
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => stop && onSave(stop, { leg_mode: mode, decide_by: mode ? null : decideBy || null, title: title.trim() || stop.title })}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export type EndKind = 'start' | 'end';

/** How you arrive at the start, or leave at the finish. */
export function EndDialog({
  kind,
  trip,
  onClose,
  onSave,
}: {
  kind: EndKind | null;
  trip: Trip;
  onClose: () => void;
  onSave: (kind: EndKind, patch: { mode: TravelMode | null; detail: string | null; time: string | null }) => void;
}) {
  const [mode, setMode] = useState<TravelMode | null>(null);
  const [detail, setDetail] = useState('');
  const [time, setTime] = useState('');

  useEffect(() => {
    if (!kind) return;
    setMode(kind === 'start' ? trip.start_mode : trip.end_mode);
    setDetail((kind === 'start' ? trip.start_detail : trip.end_detail) ?? '');
    setTime((kind === 'start' ? trip.start_time : trip.end_time)?.slice(0, 5) ?? '');
  }, [kind, trip]);

  const place = kind === 'start' ? trip.start_place : trip.end_place;
  const date = kind === 'start' ? trip.start_date : trip.end_date;

  return (
    <Dialog open={!!kind} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {kind === 'start' ? 'How you arrive' : 'How you leave'}
        {place ? ` ${kind === 'start' ? 'in' : ''} ${place}` : ''}
        {date ? ` · ${formatDay(date)}` : ''}
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <ModeChips modes={END_MODES} value={mode} onChange={setMode} />
        {mode ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField label={mode === 'flight' ? 'Airport or flight' : 'Station or stop'} value={detail} onChange={(e) => setDetail(e.target.value)} />
            <TextField
              label="Time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="Optional"
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Leave it open; it stays on your Decide later list.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => kind && onSave(kind, { mode, detail: mode ? detail.trim() || null : null, time: mode ? time || null : null })}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Where you sleep: a whole town's nights at once, or a single night. */
export function NightsDialog({
  days,
  onClose,
  onSave,
}: {
  days: Day[] | null;
  onClose: () => void;
  /** lodging is omitted when the nights had different lodgings and the field was left blank: keep them. */
  onSave: (days: Day[], patch: { base_city: string | null; lodging?: string | null; base_fixed?: boolean; in_transit?: boolean }) => void;
}) {
  const [town, setTown] = useState('');
  const [lodging, setLodging] = useState('');
  const [fixed, setFixed] = useState(false);
  // Some nights pinned and some not: shown indeterminate, and left alone on
  // save unless the traveller clicks it.
  const [fixedMixed, setFixedMixed] = useState(false);
  const [fixedTouched, setFixedTouched] = useState(false);
  const [mixed, setMixed] = useState(false);

  useEffect(() => {
    if (!days?.length) return;
    setTown(days[0].base_city ?? '');
    const l = Array.from(new Set(days.map((d) => d.lodging).filter(Boolean)));
    setLodging(l.length === 1 ? (l[0] as string) : '');
    setMixed(l.length > 1);
    setFixed(days.every((d) => d.base_fixed));
    setFixedMixed(days.some((d) => d.base_fixed) && !days.every((d) => d.base_fixed));
    setFixedTouched(false);
  }, [days]);

  const many = (days?.length ?? 0) > 1;
  const first = days?.[0];
  const last = days?.[days.length - 1];

  return (
    <Dialog open={!!days?.length} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        Where you sleep
        <Typography variant="body2" color="text.secondary">
          {first ? formatDay(first.date) : ''}
          {many && last ? ` – ${formatDay(last.date)} · ${days!.length} nights` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Town" value={town} onChange={(e) => setTown(e.target.value)} autoFocus fullWidth />
        <TextField
          label="Staying at"
          placeholder={mixed ? 'Different places — leave blank to keep them' : 'Hotel, flat, friends…'}
          value={lodging}
          onChange={(e) => setLodging(e.target.value)}
          fullWidth
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={fixed}
              indeterminate={fixedMixed && !fixedTouched}
              onChange={(e) => {
                setFixed(e.target.checked);
                setFixedTouched(true);
              }}
            />
          }
          label={
            <Box>
              <Typography variant="body2">Fixed</Typography>
              <Typography variant="caption" color="text.secondary">
                Committed to. Planning won’t change it.
              </Typography>
            </Box>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() =>
            days &&
            onSave(days, {
              base_city: town.trim() || null,
              ...(mixed && !lodging.trim() ? {} : { lodging: lodging.trim() || null }),
              ...(fixedMixed && !fixedTouched ? {} : { base_fixed: fixed && !!town.trim() }),
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
